<script lang="ts">
  import { user } from '../../stores/user';
  import { alertsHref } from '../../utils/navigation';
  import { hasPropertyCoordinates, matchesProperty } from '../../utils/propertyMap';
  import { onMount, onDestroy } from "svelte";
  import 'leaflet/dist/leaflet.css';
  import 'leaflet.markercluster/dist/MarkerCluster.css';
  import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
  import GlassCard from '../ui/GlassCard.svelte';
  import { RotateCcw, Globe, Layers, Activity } from 'lucide-svelte';
  import { unitPreference } from '../../stores/preferences';
  
  const defaultSeededBenchmarks = [
    {
      property_id: 700,
      property_name: "South Tower",
      region_market: "Greater Vancouver, BC",
      latitude: 49.261775,
      longitude: -122.89238,
      total_units: 240,
      current_flow_rate_gpm: 28.4,
      gallons_per_unit_day: 132.5,
      portfolio_benchmark_percentile: 91,
      monthly_budget_variance_pct: -6.8,
      health_status: "optimal"
    },
    {
      property_id: 706,
      property_name: "Fedora Multi-Family Residences",
      region_market: "Southern California",
      latitude: 34.053982,
      longitude: -118.298190,
      total_units: 110,
      current_flow_rate_gpm: 14.2,
      gallons_per_unit_day: 148.0,
      portfolio_benchmark_percentile: 78,
      monthly_budget_variance_pct: 1.2,
      health_status: "advisory"
    },
    {
      property_id: 714,
      property_name: "Hollywood Villas Luxury Suites",
      region_market: "Southern California",
      latitude: 34.093488,
      longitude: -118.316582,
      total_units: 85,
      current_flow_rate_gpm: 11.5,
      gallons_per_unit_day: 124.0,
      portfolio_benchmark_percentile: 95,
      monthly_budget_variance_pct: -11.4,
      health_status: "optimal"
    },
    {
      property_id: 715,
      property_name: "Phoenix Apartments Mid-Rise",
      region_market: "Southern California",
      latitude: 34.032993,
      longitude: -118.292474,
      total_units: 160,
      current_flow_rate_gpm: 34.8,
      gallons_per_unit_day: 188.5,
      portfolio_benchmark_percentile: 42,
      monthly_budget_variance_pct: 18.5,
      health_status: "critical_alarm"
    },
    {
      property_id: 745,
      property_name: "1841 Stoner Ave Garden Complex",
      region_market: "Southern California",
      latitude: 34.038696,
      longitude: -118.453143,
      total_units: 95,
      current_flow_rate_gpm: 12.8,
      gallons_per_unit_day: 139.0,
      portfolio_benchmark_percentile: 84,
      monthly_budget_variance_pct: -3.2,
      health_status: "optimal"
    }
  ];

  let { properties = [], alerts = [], height = "400px", searchable = false, gisBenchmarks = [] } = $props();
  let searchQuery = $state('');
  let showResults = $state(false);
  let locationMessage = $state('');
  let isGisHeatmapMode = $state(true);
  let loadedBenchmarks = $state<any[]>([]);

  const activeBenchmarks = $derived(gisBenchmarks && gisBenchmarks.length > 0 ? gisBenchmarks : (loadedBenchmarks.length > 0 ? loadedBenchmarks : defaultSeededBenchmarks));
  const matches = $derived(searchQuery.trim() ? properties.filter((p: any) => matchesProperty(p, searchQuery)) : []);
  const propertyMarkers = new Map<string, any>();
  let disposed = false;

  function focusProperty(property: any) {
    const marker = propertyMarkers.get(String(property.property_id));
    if (!map || !marker || !markersCluster) return;
    showResults = false;
    searchQuery = property.property_name || property.address || String(property.property_id);
    locationMessage = `Showing ${searchQuery} on the map.`;
    map.setView(marker.getLatLng(), 16);
    markersCluster.zoomToShowLayer(marker, () => { if (!disposed) marker.openPopup(); });
  }

  const storageKey = $derived(`saya_last_map_view_${$user?.user_id || 'default'}`);

  function saveMapView() {
    if (!map || disposed) return;
    try {
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (center && typeof zoom === 'number') {
        sessionStorage.setItem(storageKey, JSON.stringify({
          lat: center.lat,
          lng: center.lng,
          zoom: zoom
        }));
      }
    } catch (_) {}
  }

  function getSavedMapView() {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed.lat === 'number' && typeof parsed.lng === 'number' && typeof parsed.zoom === 'number') {
        return parsed;
      }
    } catch (_) {}
    return null;
  }

  function escapeHTML(value: unknown) {
    return String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'}[char]!));
  }

  let mapElement: HTMLElement;
  let map: any = null;
  let L: any = null;
  let isReady = $state(false);
  let resizeObserver: ResizeObserver;

  onMount(async () => {
    // Dynamically import Leaflet so it doesn't break SSR (Server-Side Rendering) if used later
    const LModule = await import('leaflet');
    if (disposed) return;
    L = LModule.default || LModule;
    (window as any).L = L; // Important for plugins like markercluster to find L
    await import('leaflet.markercluster'); // Attach marker cluster prototype to L
    if (disposed) return;

    // Fix for missing default icon in Leaflet bundled with bundlers
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    });

    const effectiveList = (properties && properties.length > 0) ? properties : activeBenchmarks;
    const savedView = getSavedMapView();
    const validProps = effectiveList.filter(hasPropertyCoordinates);
    let initialCenter: [number, number] = [37.7749, -122.4194];
    let initialZoom = 11;

    if (validProps.length === 1) {
      // Single property user (e.g. Matt Boffo at South Tower): center directly on property coordinates!
      initialCenter = [Number(validProps[0].latitude), Number(validProps[0].longitude)];
      initialZoom = 13;
    } else if (validProps.length > 1) {
      // Multiple properties: default to centroid if no valid saved view
      const avgLat = validProps.reduce((sum: number, p: any) => sum + Number(p.latitude), 0) / validProps.length;
      const avgLng = validProps.reduce((sum: number, p: any) => sum + Number(p.longitude), 0) / validProps.length;
      initialCenter = [avgLat, avgLng];
      initialZoom = validProps.length > 10 ? 4 : 10;
    }

    if (savedView) {
      const isNearSavedView = validProps.some((p: any) => {
        return Math.abs(p.latitude - savedView.lat) < 6 && Math.abs(p.longitude - savedView.lng) < 6;
      });
      if (isNearSavedView) {
        initialCenter = [savedView.lat, savedView.lng];
        initialZoom = savedView.zoom;
      } else {
        // Discard stale savedView from another region (e.g. admin California view when viewing Vancouver property)
        try { sessionStorage.removeItem(storageKey); } catch (_) {}
      }
    }

    map = L.map(mapElement, {
      maxBounds: [[-85, -180], [85, 180]],
      maxBoundsViscosity: 1.0,
      worldCopyJump: false
    }).setView(initialCenter, initialZoom);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    map.on('moveend', saveMapView);
    map.on('zoomend', saveMapView);

    isReady = true; // Signals effect layer that Leaflet map engine is physically attached without Proxy nesting

    resizeObserver = new ResizeObserver(() => {
      if (map) map.invalidateSize();
    });
    resizeObserver.observe(mapElement);

    // Initial render pass
    updateMarkers();

    // Secondary delayed passes to ensure map dimensions are synced
    setTimeout(() => {
      if (!disposed && map) {
        map.invalidateSize();
      }
    }, 150);

    setTimeout(() => {
      if (!disposed && map) {
        map.invalidateSize();
      }
    }, 450);
  });

  onDestroy(() => {
    disposed = true;
    if (resizeObserver) resizeObserver.disconnect();
    if (map) map.remove();
  });

  const isMetric = $derived($unitPreference === 'metric');

  $effect(() => {
    // Svelte 5 $effect tracks arrays and props
    let _l1 = properties?.length || 0;
    let _l2 = alerts?.length || 0;
    let _l3 = activeBenchmarks?.length || 0;
    let _mode = isGisHeatmapMode;
    let _r = isReady;
    let _pref = $unitPreference;
    if (_r && map && L) {
      updateMarkers();
    }
  });

  let markersCluster: any;

  function applyBoundsAndZoomLimit(targetBounds: any, isDefault: boolean, force = false) {
    if (!map || !L) return;
    const savedView = getSavedMapView();
    const effectiveList = (properties && properties.length > 0) ? properties : activeBenchmarks;

    if (savedView && !force) {
      // Check if current properties are within reasonable range of saved view
      const isNearSavedView = effectiveList.some((p: any) => {
        if (!hasPropertyCoordinates(p)) return false;
        return Math.abs(p.latitude - savedView.lat) < 6 && Math.abs(p.longitude - savedView.lng) < 6;
      });
      if (isNearSavedView) {
        // Respect user's saved view within the current region
        return;
      }
    }
    if (isDefault) {
      map.setMinZoom(2);
      map.fitBounds(targetBounds, { padding: [10, 10] });
      return;
    }
    const ne = targetBounds.getNorthEast();
    const sw = targetBounds.getSouthWest();
    if (ne.distanceTo(sw) < 500) {
      // Single property or tightly clustered within 500m
      map.setMinZoom(10);
      map.fitBounds(targetBounds, { padding: [50, 50], maxZoom: 14 });
    } else {
      // Multiple distributed properties: calculate zoom level that fits all of them
      const boundsZoom = map.getBoundsZoom(targetBounds, false, [50, 50]);
      map.setMinZoom(Math.max(2, boundsZoom));
      map.fitBounds(targetBounds, { padding: [50, 50], maxZoom: 14 });
    }
  }

  function resetMap() {
    searchQuery = '';
    showResults = false;
    locationMessage = '';
    try {
      sessionStorage.removeItem(storageKey);
    } catch (_) {}
    if (map && L) {
      let bounds = L.latLngBounds();
      let hasValidCoords = false;
      const effectiveList = (properties && properties.length > 0) ? properties : activeBenchmarks;
      effectiveList.forEach((p: any) => {
        if (hasPropertyCoordinates(p)) {
          bounds.extend([p.latitude, p.longitude]);
          hasValidCoords = true;
        }
      });
      if (hasValidCoords && bounds.isValid()) {
        applyBoundsAndZoomLimit(bounds, false, true);
      }
    }
  }

  function updateMarkers() {
    if (!map || !L) return;
    
    let clusterEngine = (L as any).markerClusterGroup || (window as any).L?.markerClusterGroup;
    if (!clusterEngine || !properties) return;

    if (!markersCluster) {
      markersCluster = clusterEngine({
        maxClusterRadius: 40,
        spiderfyOnMaxZoom: true,
        zoomToBoundsOnClick: true,
        iconCreateFunction: function(cluster: any) {
          let childMarkers = cluster.getAllChildMarkers();
          let hasCritical = false;
          let hasWarning = false;
          
          childMarkers.forEach((marker: any) => {
             if (marker.options.alertStatus === 'critical') hasCritical = true;
             else if (marker.options.alertStatus === 'warning') hasWarning = true;
          });

          let haloColor = 'rgba(148, 163, 184, 0.6)'; // gray base
          let haloClass = 'cluster-halo-standard';
          let textColor = '#fff';

          if (hasCritical) {
            haloClass = 'cluster-halo-critical';
            haloColor = 'rgba(239, 68, 68, 0.8)';
          } else if (hasWarning) {
            haloClass = 'cluster-halo-warning';
            haloColor = 'rgba(245, 158, 11, 0.8)';
          } else {
            // If all active nodes, light blue/green
            haloColor = 'rgba(34, 197, 94, 0.8)';
            haloClass = 'cluster-halo-active';
          }

          return L.divIcon({
            html: `<div class="${haloClass}" style="width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); background-color: ${haloColor}; border: 2px solid rgba(255,255,255,0.4); color: ${textColor}; font-weight: 900; font-family: Inter, sans-serif; text-shadow: 0 1px 2px rgba(0,0,0,0.5);">
                     ${childMarkers.length}
                   </div>`,
            className: 'custom-cluster-icon', 
            iconSize: [34, 34]
          });
        }
      });
      map.addLayer(markersCluster);
    }

    // Cleanly wipe existing markers without dropping cluster engine
    markersCluster.clearLayers();
    propertyMarkers.clear();
    
    // Fallback remove old raw markers just in case
    map.eachLayer((layer: any) => {
      if (layer instanceof L.Marker && !layer._isCluster) {
        map.removeLayer(layer);
      }
    });

    let bounds = L.latLngBounds();
    let hasValidCoords = false;

    // Default to North America if no bounds
    const defaultBounds = L.latLngBounds([
      [24.396308, -125.000000], // roughly bottom left (Mexico/Florida)
      [49.384358, -66.934570]   // roughly top right (Canada/Maine)
    ]);

    const activeIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: #22c55e; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 8px rgba(0,0,0,0.4);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9]
    });

    const inactiveIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: #94a3b8; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 8px rgba(0,0,0,0.4);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9]
    });

    const criticalIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: #ef4444; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9]
    });

    const warningIcon = L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: #f59e0b; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9]
    });

    const propMap = new Map();
    properties.forEach((p: any) => propMap.set(String(p.property_id), { ...p }));

    if (properties && properties.length > 0) {
      // Scoped to authorized properties: only enrich coordinates & benchmark telemetry for properties passed in
      activeBenchmarks.forEach((b: any) => {
        const key = String(b.property_id);
        if (propMap.has(key)) {
          const existing = propMap.get(key);
          if (!hasPropertyCoordinates(existing) && hasPropertyCoordinates(b)) {
            existing.latitude = b.latitude;
            existing.longitude = b.longitude;
          }
        }
      });
    } else {
      // Only fallback to standalone default benchmarks when properties array was not passed or is empty
      activeBenchmarks.forEach((b: any) => {
        const key = String(b.property_id);
        if (!propMap.has(key)) {
          propMap.set(key, {
            property_id: b.property_id,
            property_name: b.property_name,
            latitude: b.latitude,
            longitude: b.longitude,
            device_count: b.total_monitored_devices || 1,
            unit_count: b.total_units || 100,
            active_alarms: b.active_anomaly_count || 0
          });
        }
      });
    }

    const mergedProperties = Array.from(propMap.values());

    mergedProperties.forEach((prop: any) => {
      if (hasPropertyCoordinates(prop)) {
        let latLng = [prop.latitude, prop.longitude];
        hasValidCoords = true;
        bounds.extend(latLng);

        const benchmark = activeBenchmarks.find((b: any) => String(b.property_id) === String(prop.property_id));

        if (isGisHeatmapMode && benchmark) {
          const pinColor = benchmark.health_status === 'optimal' 
            ? '#10b981' 
            : benchmark.health_status === 'advisory' 
            ? '#f59e0b' 
            : '#ef4444';

          const gisIcon = L.divIcon({
            className: 'custom-gis-icon',
            html: `
              <div style="background: ${pinColor}; color: white; border-radius: 9999px; padding: 3px 8px; font-weight: 800; font-size: 11px; box-shadow: 0 4px 14px rgba(0,0,0,0.35); border: 2px solid white; display: flex; align-items: center; gap: 5px; white-space: nowrap; transform: translate(-50%, -50%); cursor: pointer; text-shadow: 0 1px 2px rgba(0,0,0,0.5);">
                <span style="width: 7px; height: 7px; border-radius: 50%; background: #ffffff; display: inline-block;"></span>
                <span>${benchmark.portfolio_benchmark_percentile}th %ile</span>
                <span style="opacity: 0.9; font-weight: 600; font-size: 10px;">${isMetric ? (benchmark.current_flow_rate_gpm * 3.78541).toFixed(1) + ' L/min' : benchmark.current_flow_rate_gpm.toFixed(1) + ' GPM'}</span>
              </div>
            `,
            iconSize: [120, 24],
            iconAnchor: [60, 12]
          });

          const propertyUrl = `#/properties/${encodeURIComponent(prop.property_id)}`;
          const popupContent = `
            <div class="px-2 py-1 min-w-[210px] text-xs">
              <div class="flex items-center justify-between gap-2 mb-1.5">
                <span class="badge badge-sm font-black ${benchmark.portfolio_benchmark_percentile >= 90 ? 'badge-primary' : benchmark.portfolio_benchmark_percentile >= 75 ? 'badge-success' : 'badge-warning'}">
                  EPA WaterScore ${benchmark.portfolio_benchmark_percentile}th %ile
                </span>
                <span class="text-[10px] text-base-content/60 font-semibold uppercase">${benchmark.region_market || 'Regional'}</span>
              </div>
              <a href="${propertyUrl}" class="font-black text-sm text-primary hover:underline block mb-1">
                ${escapeHTML(prop.property_name || benchmark.property_name)} &rarr;
              </a>
              <div class="text-[11px] text-base-content/70 mb-2">${escapeHTML(prop.address || benchmark.property_name)}</div>
              
              <div class="grid grid-cols-2 gap-1.5 p-2 bg-base-200/60 rounded-xl mb-2 text-[11px]">
                <div>
                  <span class="text-base-content/50 block text-[9px] uppercase font-bold">Current Flow</span>
                  <span class="font-mono font-bold text-info">${isMetric ? (benchmark.current_flow_rate_gpm * 3.78541).toFixed(1) + ' L/min' : benchmark.current_flow_rate_gpm.toFixed(1) + ' GPM'}</span>
                </div>
                <div>
                  <span class="text-base-content/50 block text-[9px] uppercase font-bold">${isMetric ? 'Daily LUD' : 'Daily GUD'}</span>
                  <span class="font-mono font-bold text-emerald-500">${isMetric ? (benchmark.gallons_per_unit_day * 3.78541).toFixed(1) + ' L' : benchmark.gallons_per_unit_day.toFixed(1) + ' gal'}</span>
                </div>
                <div>
                  <span class="text-base-content/50 block text-[9px] uppercase font-bold">Budget Var.</span>
                  <span class="font-mono font-bold ${benchmark.monthly_budget_variance_pct <= 0 ? 'text-success' : 'text-warning'}">
                    ${benchmark.monthly_budget_variance_pct > 0 ? '+' : ''}${benchmark.monthly_budget_variance_pct.toFixed(1)}%
                  </span>
                </div>
                <div>
                  <span class="text-base-content/50 block text-[9px] uppercase font-bold">Health Status</span>
                  <span class="font-bold uppercase text-[10px] ${benchmark.health_status === 'optimal' ? 'text-success' : benchmark.health_status === 'advisory' ? 'text-warning' : 'text-error'}">
                    ${benchmark.health_status.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <div class="pt-1.5 border-t border-base-200/80">
                <a href="${propertyUrl}" class="btn btn-xs btn-primary font-bold rounded-lg w-full flex items-center justify-center gap-1 shadow-xs">
                  Open Workspace &rarr;
                </a>
              </div>
            </div>
          `;

          let marker = L.marker(latLng, { 
            icon: gisIcon,
            alertStatus: benchmark.health_status === 'critical_alarm' ? 'critical' : (benchmark.health_status === 'advisory' ? 'warning' : 'none')
          }).bindPopup(popupContent, { className: 'custom-popup rounded-2xl border-none shadow-xl' });
            
          markersCluster.addLayer(marker);
          propertyMarkers.set(String(prop.property_id), marker);
        } else {
          let propAlerts = alerts.filter(a => a.property_id === prop.property_id);
          let hasCritical = propAlerts.some(a => a.flow_alarm > 0);
          let hasWarning = propAlerts.length > 0 && !hasCritical;

          let statusHtml = '';
          if (hasCritical) {
              statusHtml = `<a href="${alertsHref($user?.user_type)}" class="text-error font-bold text-xs uppercase block hover:underline transition-colors mt-1">🔴 Critical Alerts &rarr;</a>`;
          } else if (hasWarning) {
              statusHtml = `<a href="${alertsHref($user?.user_type)}" class="text-warning font-bold text-xs uppercase block hover:underline transition-colors mt-1">🟡 Active Warnings &rarr;</a>`;
          } else if (prop.device_count > 0) {
              statusHtml = `<span class="text-success font-bold text-xs uppercase block mt-1">🟢 ${prop.device_count} Active Nodes</span>`;
          } else {
              statusHtml = `<span class="text-base-content/50 font-bold text-xs uppercase block mt-1">⚪ No Nodes</span>`;
          }

          let propertyUrl = `#/admin/properties/${encodeURIComponent(prop.property_id)}`;
          let popupContent = `
            <div class="px-2 py-1 min-w-[160px]">
              <a href="${propertyUrl}" class="font-black text-sm text-primary hover:underline block mb-1">
                ${escapeHTML(prop.property_name || 'Unnamed Property')} &rarr;
              </a>
              <div class="text-xs text-base-content/70 mb-2">${escapeHTML(prop.address || '')} ${escapeHTML(prop.city || '')}, ${escapeHTML(prop.state || '')}</div>
              ${statusHtml}
              <div class="mt-2.5 pt-2 border-t border-base-200/80">
                <a href="${propertyUrl}" class="btn btn-xs btn-primary font-bold rounded-lg w-full flex items-center justify-center gap-1 shadow-xs">
                  Open Workspace &rarr;
                </a>
              </div>
            </div>
          `;

          let markerIcon = hasCritical ? criticalIcon : (hasWarning ? warningIcon : (prop.device_count > 0 ? activeIcon : inactiveIcon));

          let marker = L.marker(latLng, { 
            icon: markerIcon,
            alertStatus: hasCritical ? 'critical' : (hasWarning ? 'warning' : 'none')
          }).bindPopup(popupContent, { className: 'custom-popup rounded-2xl border-none shadow-xl' });
            
          markersCluster.addLayer(marker);
          propertyMarkers.set(String(prop.property_id), marker);
        }
      }
    });

    if (hasValidCoords) {
      applyBoundsAndZoomLimit(bounds, false);
    } else {
      applyBoundsAndZoomLimit(defaultBounds, true);
    }
  }
</script>

{#if searchable}
  <div class="mb-4 px-2">
    <label class="block text-sm font-semibold">
      Search properties on map
      <input type="search" class="input w-full mt-2" placeholder="Property name, address, city, or ID" bind:value={searchQuery}
        oninput={() => { showResults = true; locationMessage = ''; }}
        onfocus={() => showResults = true}
        onkeydown={(event) => { if (event.key === 'Escape') showResults = false; }} />
    </label>
    {#if showResults && searchQuery.trim()}
      <div class="mt-2 max-h-60 overflow-y-auto rounded-xl border border-base-300 bg-base-100" aria-label="Property search results">
        {#each matches as property (property.property_id)}
          <div class="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-base-200 last:border-0">
            <div class="min-w-0">
              {#if hasPropertyCoordinates(property)}
                <button class="text-left font-semibold link link-primary" disabled={!isReady} onclick={() => focusProperty(property)}>{property.property_name || `Property #${property.property_id}`}</button>
              {:else}
                <span class="font-semibold">{property.property_name || `Property #${property.property_id}`}</span>
                <span class="text-xs text-base-content/65 ml-2">Location unavailable</span>
              {/if}
              <p class="text-xs text-base-content/65">{[property.address, property.city, property.state].filter(Boolean).join(', ')} · #{property.property_id}</p>
            </div>
            <a class="link text-sm" href={`#/admin/properties/${property.property_id}`}>View property<span class="sr-only"> {property.property_name || property.property_id}</span></a>
          </div>
        {:else}
          <p class="p-3 text-sm" role="status">No matching properties. Try another name, address, or ID.</p>
        {/each}
      </div>
    {/if}
    {#if locationMessage}<p class="mt-2 text-sm text-base-content/65" role="status">{locationMessage}</p>{/if}
  </div>
{/if}
<div class="property-map-container w-full relative z-0 rounded-[2rem] overflow-hidden shadow-inner group" style="height: {height};">
  <div bind:this={mapElement} class="w-full h-full bg-base-300"></div>

  <!-- Layer Mode Switcher Overlay -->
  <div class="absolute top-4 left-4 z-[400] flex items-center gap-1.5 bg-base-100/90 backdrop-blur-md p-1 rounded-2xl shadow-lg border border-base-200/80">
    <button 
      type="button"
      class="btn btn-xs rounded-xl font-bold gap-1.5 {isGisHeatmapMode ? 'btn-primary text-white shadow-xs' : 'btn-ghost text-base-content/70'}"
      onclick={() => { isGisHeatmapMode = true; updateMarkers(); }}
    >
      <Globe class="w-3.5 h-3.5" />
      <span>GIS WaterScore Heatmap</span>
    </button>
    <button 
      type="button"
      class="btn btn-xs rounded-xl font-bold gap-1.5 {!isGisHeatmapMode ? 'btn-primary text-white shadow-xs' : 'btn-ghost text-base-content/70'}"
      onclick={() => { isGisHeatmapMode = false; updateMarkers(); }}
    >
      <Activity class="w-3.5 h-3.5" />
      <span>Device Nodes</span>
    </button>
  </div>

  <!-- GIS Heatmap Legend Overlay -->
  {#if isGisHeatmapMode}
    <div class="absolute bottom-4 left-4 z-[400] bg-base-100/90 backdrop-blur-md px-3 py-1.5 rounded-2xl shadow-lg border border-base-200/80 flex items-center gap-3 text-[10px] font-bold">
      <span class="text-base-content/50 uppercase tracking-wider text-[9px]">EPA WaterScore:</span>
      <span class="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
        <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span> Optimal (&gt;80th)
      </span>
      <span class="flex items-center gap-1 text-amber-600 dark:text-amber-400">
        <span class="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block"></span> Advisory (60-80th)
      </span>
      <span class="flex items-center gap-1 text-rose-600 dark:text-rose-400">
        <span class="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block"></span> Critical (&lt;60th)
      </span>
    </div>
  {/if}
  
  <div class="absolute bottom-4 right-4 z-[400] opacity-0 group-hover:opacity-100 transition-opacity">
    <button class="btn btn-circle btn-primary shadow-xl btn-sm" onclick={resetMap} title="Reset View">
      <RotateCcw class="w-4 h-4" />
    </button>
  </div>
</div>

<style>
  :global(.leaflet-container) {
    background: #cad2d3 !important; /* OSM water color */
    font-family: inherit;
  }
  
  @media (prefers-color-scheme: dark) {
    :global(:root:not([data-theme="light"]) .property-map-container .leaflet-layer) {
      filter: brightness(0.7) contrast(1.1);
    }
  }
  :global([data-theme="dark"] .property-map-container .leaflet-layer) {
    filter: brightness(0.7) contrast(1.1);
  }

  :global(.leaflet-popup-content-wrapper) {
    background-color: oklch(var(--b1) / 0.98); /* Less transparent, more readable */
    backdrop-filter: blur(12px);
    color: var(--fallback-bc,oklch(var(--bc)));
    border-radius: 1.5rem;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255,255,255,0.05);
  }
  
  :global(.leaflet-popup-tip) {
    background-color: var(--fallback-b1,oklch(var(--b1)));
    opacity: 0.98;
  }

  /* Fix for cluster hover tooltips or native hover tooltips */
  :global(.leaflet-tooltip) {
    background-color: oklch(var(--n) / 0.95);
    backdrop-filter: blur(8px);
    color: var(--fallback-nc,oklch(var(--nc)));
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 0.75rem;
    font-weight: 700;
    box-shadow: 0 4px 10px rgba(0,0,0,0.4);
  }
  
  :global(.leaflet-tooltip-top:before) { border-top-color: oklch(var(--n) / 0.95); }
  :global(.leaflet-tooltip-bottom:before) { border-bottom-color: oklch(var(--n) / 0.95); }
  :global(.leaflet-tooltip-left:before) { border-left-color: oklch(var(--n) / 0.95); }
  :global(.leaflet-tooltip-right:before) { border-right-color: oklch(var(--n) / 0.95); }

  :global(.cluster-halo-critical) {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    animation: map-pulse-critical 2s infinite;
  }
  
  :global(.cluster-halo-warning) {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
    animation: map-pulse-warning 2s infinite;
  }

  @keyframes map-pulse-critical {
    0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
    70% { box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); }
    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
  }

  @keyframes map-pulse-warning {
    0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7); }
    70% { box-shadow: 0 0 0 12px rgba(245, 158, 11, 0); }
    100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
  }

  :global(.custom-popup .leaflet-popup-content) {
    margin: 10px 14px;
    line-height: 1.4;
  }
</style>
